import clsx from 'clsx';
import { Button as ButtonPrimitive } from '@base-ui/react/button';
import type { ReactNode } from 'react';
import React from 'react';
import useTestTag from '../../common/hooks/use-test-tag';
import OdinIcon, { OdinIconSize, type OdinIconName } from '../odin-icon/odin-icon.component';

export type ButtonType = 'default' | 'primary' | 'text';
export type ButtonVariant = 'regular' | 'alternative';
export type ButtonHtmlType = 'submit' | 'reset' | 'button';

export interface ButtonProps extends Omit<ButtonPrimitive.Props, 'type'> {
  type?: ButtonType;
  variant?: ButtonVariant;
  classNames?: string;
  fullWidth?: boolean;
  hidden?: boolean;
  disabled?: boolean;
  htmlType?: ButtonHtmlType;
}

interface IconButtonProps extends Omit<ButtonProps, 'type' | 'children'> {
  type?: ButtonHtmlType;
  icon: OdinIconName;
  iconSize?: OdinIconSize;
  iconClassName?: string;
  spin?: boolean;
  showFocus?: boolean;
  template?: (props: IconButtonProps, icon: React.ReactNode) => React.ReactNode;
  /** Optional tooltip text; applied to title & aria-label for accessibility */
  tooltip?: string;
  /**
   * Optional explicit children (custom content or icon). Precedence order for rendering is:
   * children > template(props, iconElement) > default <OdinIcon />.
   * Providing children completely bypasses the template and default icon rendering.
   */
  children?: ReactNode;
}

const commonButtonStyles =
  'odin-inline-flex sm:odin-w-auto odin-text-md odin-leading-5 odin-rounded focus:odin-outline-none odin-justify-center odin-transition-colors odin-duration-200 odin-min-w-[3rem] odin-min-h-[2.25rem] odin-py-2 odin-px-5 odin-font-medium focus:odin-ring-2 focus:odin-ring-offset-2';

const buttonStylesMap: Record<ButtonType, Record<ButtonVariant, string>> = {
  primary: {
    regular:
      'odin-bg-primary-2 hover:odin-bg-primary-3 odin-text-mono-1 focus:odin-ring-offset-mono-1 focus:odin-ring-primary-2',
    alternative: 'odin-bg-primary-1 hover:odin-bg-primary-2 odin-text-mono-1 focus:odin-ring-primary-1',
  },
  default: {
    regular:
      'odin-bg-mono-1 hover:odin-bg-primary-5 odin-text-primary-2 odin-border odin-border-solid odin-border-primary-2 focus:odin-ring-offset-mono-1 focus:odin-ring-primary-2',
    alternative:
      'hover:odin-bg-primary-5 odin-text-primary-1 odin-border odin-border-primary-1 focus:odin-ring-primary-1',
  },
  text: {
    regular: 'odin-text-primary-1 hover:odin-text-primary-3 odin-bg-transparent focus:odin-ring-primary-3',
    alternative:
      'odin-text-primary-1 hover:odin-text-primary-3 odin-bg-transparent focus:odin-ring-[0px] focus:odin-ring-offset-0',
  },
};

const getButtonStyles = (type: ButtonType, variant: ButtonVariant) => {
  const styles = buttonStylesMap[type] || buttonStylesMap.default;
  return styles[variant];
};

export default function Button({
  type = 'default',
  variant = 'regular',
  classNames,
  className,
  fullWidth = true,
  hidden = false,
  disabled = false,
  htmlType = 'button',
  form,
  ...props
}: ButtonProps) {
  const buttonStyles = clsx(
    commonButtonStyles,
    { 'odin-hidden': hidden },
    { 'odin-w-full': fullWidth },
    { 'odin-opacity-50': disabled },
    getButtonStyles(type, variant),
    classNames,
    className
  );

  return (
    <ButtonPrimitive
      data-slot="button"
      type={htmlType}
      className={buttonStyles}
      form={form}
      disabled={disabled}
      {...props}
    />
  );
}

export function IconButton({
  onClick,
  classNames,
  type = 'button',
  form,
  disabled,
  icon,
  iconSize = OdinIconSize.Small,
  iconClassName,
  spin = false,
  showFocus = true,
  template,
  tooltip,
  children,
  ...rest
}: IconButtonProps) {
  const tagCreator = useTestTag('icon-button');
  const buttonStyles = clsx({ 'odin-opacity-50 odin-cursor-not-allowed': disabled }, classNames);

  const iconElement = (
    <OdinIcon icon={icon} size={iconSize} className={iconClassName} spin={spin} showFocus={showFocus} />
  );

  // If a template is provided, use it to render the button content
  const templateProps: IconButtonProps = {
    onClick,
    classNames,
    type,
    form,
    disabled,
    icon,
    iconSize,
    iconClassName,
    spin,
    showFocus,
    template,
    ...rest,
  };

  let content: React.ReactNode;
  if (children) {
    // If explicit children passed, use them (backwards compatibility with older API)
    content = children;
  } else if (template) {
    content = template(templateProps, iconElement);
  } else {
    content = iconElement;
  }

  return (
    <button
      type={type}
      onClick={onClick}
      className={buttonStyles}
      form={form}
      disabled={disabled}
      ref={tagCreator(icon.trim() ? icon.replace(/\s+/g, '-').toLowerCase() : '')}
      title={tooltip}
      aria-label={tooltip}
    >
      {content}
    </button>
  );
}

export function Link({ classNames, className, type: _type, ...props }: ButtonProps) {
  const linkStyles = clsx(
    'odin-p-0 odin-text-primary-2 odin-underline hover:odin-no-underline odin-text-md odin-leading-5 odin-m-1 odin-bg-transparent focus:odin-outline-primary-3 focus:odin-outline-offset-4',
    classNames,
    className
  );

  return <button className={linkStyles} type="button" {...props} />;
}
